import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { AUTH_MESSAGE_CODES, AppError, MessageScopeType, MessageService, MessageType } from "@obg/ngx.core";
import { merge, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { LOGIN_ACTION_TYPE, LoginAction } from "./login.action";
import { LOGIN_MESSAGE_CONTEXT } from "./login.const";
import { ResetPasswordBySmsModel, ResetPasswordWithTokenModel } from "./login.model";
import { LoginService } from "./login.service";

@Injectable()
export class LoginEffect {
	resetPasswordWithToken$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGIN_ACTION_TYPE.resetPasswordWithToken),
			map((x: ActionWithPayload<ResetPasswordWithTokenModel>) => x.payload),
			switchMap((model) =>
				this.service.resetPasswordWithToken(model).pipe(
					map(() => this.action.resetPasswordWithTokenSuccess()),
					catchError((error: AppError) => of(this.action.resetPasswordWithTokenFail(error)))
				)
			)
		)
	);

	resetPasswordByEmail$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGIN_ACTION_TYPE.resetPasswordByEmail),
			map((x: ActionWithPayload<string>) => x.payload),
			switchMap((email) =>
				this.service.resetPasswordByEmail(email).pipe(
					map(() => this.action.resetPasswordByEmailSuccess()),
					catchError((error: AppError) => of(this.action.resetPasswordByEmailFail(error)))
				)
			)
		)
	);

	resetPasswordBySms$ = createEffect(() =>
		this.actions$.pipe(
			ofType(LOGIN_ACTION_TYPE.resetPasswordBySms),
			map((x: ActionWithPayload<ResetPasswordBySmsModel>) => x.payload),
			switchMap((payload) =>
				this.service.resetPasswordBySms(payload).pipe(
					map(() => this.action.resetPasswordBySmsSuccess()),
					catchError((error: AppError) => of(this.action.resetPasswordBySmsFail(error)))
				)
			)
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[LOGIN_ACTION_TYPE.resetPasswordWithTokenFail]: LOGIN_MESSAGE_CONTEXT.resetPasswordWithToken,
				[LOGIN_ACTION_TYPE.resetPasswordByEmailFail]: LOGIN_MESSAGE_CONTEXT.resetPasswordByEmail,
				[LOGIN_ACTION_TYPE.resetPasswordBySmsFail]: LOGIN_MESSAGE_CONTEXT.resetPasswordBySms,
			}),
			this.messageService.mapClearByContext$({
				[LOGIN_ACTION_TYPE.resetPasswordWithToken]: LOGIN_MESSAGE_CONTEXT.resetPasswordWithToken,
				[LOGIN_ACTION_TYPE.resetPasswordByEmail]: LOGIN_MESSAGE_CONTEXT.resetPasswordByEmail,
				[LOGIN_ACTION_TYPE.resetPasswordBySms]: LOGIN_MESSAGE_CONTEXT.resetPasswordBySms,
			}),
			this.messageService.map$({
				[LOGIN_ACTION_TYPE.resetPasswordWithTokenSuccess]: {
					context: LOGIN_MESSAGE_CONTEXT.resetPasswordWithToken,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_MESSAGE_CODES.changePassword.success,
				},
				[LOGIN_ACTION_TYPE.resetPasswordByEmailSuccess]: {
					context: LOGIN_MESSAGE_CONTEXT.resetPasswordByEmail,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_MESSAGE_CODES.forgotPassword.success,
				},
				[LOGIN_ACTION_TYPE.resetPasswordBySmsSuccess]: {
					context: LOGIN_MESSAGE_CONTEXT.resetPasswordBySms,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: AUTH_MESSAGE_CODES.forgotPassword.success,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: LoginAction,
		private service: LoginService,
		private messageService: MessageService
	) {}
}
